import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronRight, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { StatusIcon, toneText } from "@/components/ui/status-icon";
import { plural, useWordsVersion } from "@/api/words";
import { formatCount } from "@/money/format";

import { uk } from "../i18n/uk";

import { announce } from "./announce";
import type { AttentionAnswer } from "./api";
import { rowOf } from "./attention";
import { pillClass } from "./count-pill";
import { MERGED_INTO, pathOf } from "./nav-paths";

// «Потребує уваги» — одна плашка внизу меню (решение 066).
//
// ⚠ Только активные неразобранные дела, пришедшие с сервера; все решены — плашки
// нет. Крестик сворачивает её в строку «Потребує уваги · N справ» на том же месте,
// клик по строке раскрывает. Число в заголовке — число видов дел, и база названа
// словом «справ» (правило 5); у числа в строке дела тоже своя единица («40
// постачальників»). Новое дело (человек его ещё не видел) раскрывает плашку само,
// стоит с меткой «нове» и объявляется чтецу в живой области оболочки (`announce`),
// фокус при этом не двигается. «Видел» уходит на сервер при сворачивании, при
// переходе по строке дела и при любом уходе, пока плашка стоит раскрытой: показанное
// человек увидел. Пока ответ не пришёл, дело считается виденным здесь же — плашка не
// прыгает. Не записалось — следующий показ снова назовёт его новым.
//
// ⚠ Тело плашки стоит в DOM всегда (свёрнутое — `hidden`), а фокус при смене вида
// переносится на соседнюю кнопку: нажатая кнопка при раскрытии и сворачивании
// пропадает, и без переноса фокус падал бы на `body`. Высота списка ограничена:
// дальше он прокручивается внутри плашки, меню не растёт.

const NONE: readonly never[] = [];

// ⚠ Одна сетка с меню (решение 067): подложка плашки — на 8 px от края (рамка кольцом внутрь,
// на раскладку не влияет), значок и текст — на 16 px, свёрнутая плашка и заголовок — 32 px высотой,
// точка дела стоит в коробке 16 px на линии значка заголовка, подсветка строки — во всю плашку.
const PLATE = "rounded-control bg-raised ring-1 ring-inset ring-line";

/** Заголовок плашки — капсом, как заголовки разделов меню; число дел — в кружке рядом (картинка Σ). */
const HEAD = "flex items-center gap-xs text-2xs font-semibold tracking-wide uppercase";

/** Число дел в кружке; база («справ») — в имени заголовка для чтеца (правило 5), поэтому кружок `aria-hidden`. */
function CountDot({ n }: { readonly n: string }) {
  return (
    <span aria-hidden="true" data-numbers className={`${pillClass("neutral")} tracking-normal`}>
      {n}
    </span>
  );
}

export function AttentionPlate({
  answer,
  onSeen,
  onGo,
}: {
  readonly answer: AttentionAnswer | null;
  readonly onSeen: (keys: readonly string[]) => void;
  readonly onGo: () => void;
}) {
  useWordsVersion();
  const id = useId();
  const at = useRouterState({ select: (one) => one.location.pathname });
  const [manual, setManual] = useState(false);
  const [acked, setAcked] = useState<ReadonlySet<string>>(new Set());
  const lineRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const focusNext = useRef<"line" | "close" | null>(null);
  const told = useRef(0);
  const was = useRef(at);
  const items = answer !== null && answer.kind === "items" ? answer.items : NONE;

  // Решённое дело забывается и здесь: вернувшееся снова будет новым.
  useEffect(() => {
    setAcked((have) => {
      const live = new Set<string>(items.map((one) => one.key));
      const next = new Set([...have].filter((key) => live.has(key)));
      return next.size === have.size ? have : next;
    });
  }, [items]);

  const isNew = (one: (typeof items)[number]) => one.new && !acked.has(one.key);
  const fresh = items.filter(isNew).length;
  const expanded = manual || fresh > 0;

  function markAll(): void {
    if (!items.some(isNew)) return;
    setAcked((have) => new Set([...have, ...items.map((one) => one.key)]));
    onSeen(items.map((one) => one.key));
  }
  const latest = useRef(markAll);
  latest.current = markAll;

  // Новые дела раскрыли плашку без единого нажатия — чтецу говорим словами.
  useEffect(() => {
    if (fresh > told.current) announce(plural(uk.attention.live, fresh, formatCount(fresh)));
    told.current = fresh;
  }, [fresh]);

  // Ушли на другой экран или закрыли вкладку, пока плашка раскрыта: показанное увидено.
  useEffect(() => {
    if (was.current === at) return;
    was.current = at;
    if (expanded) latest.current();
  }, [at, expanded]);
  useEffect(() => {
    const leave = () => latest.current();
    window.addEventListener("pagehide", leave);
    return () => window.removeEventListener("pagehide", leave);
  }, []);

  useEffect(() => {
    const to = focusNext.current === "close" ? closeRef.current : focusNext.current === "line" ? lineRef.current : null;
    focusNext.current = null;
    to?.focus();
  }, [expanded]);

  if (answer === null) return null;
  if (answer.kind === "silent") {
    // Не «дел нет», а «не знаю»: молчание обращения не равно пустоте (правило 10).
    return (
      <p role="status" className={`${PLATE} p-xs text-sm text-ink-2`}>
        {uk.attention.silent}
      </p>
    );
  }
  if (items.length === 0) return null;

  const alarm = items.some((one) => one.tone === "alarm");
  // Тон заголовка — по самому срочному делу: авария (просрочка, сбой) или предупреждение.
  const tone = alarm ? "alarm" : "warn";
  const title = plural(uk.attention.summary, items.length, formatCount(items.length));
  const bodyId = `${id}-body`;
  const headId = `${id}-head`;

  /** Ушли по строке дела: оно увидено. Серверу — весь список виденного, а не одно дело. */
  function markOne(key: string): void {
    if (!items.some((one) => one.key === key && isNew(one))) return;
    setAcked((have) => new Set([...have, key]));
    onSeen([...items.filter((one) => !isNew(one)).map((one) => one.key), key]);
  }

  return (
    <div>
      {expanded ? null : (
        <button
          ref={lineRef}
          type="button"
          aria-expanded={false}
          aria-controls={bodyId}
          aria-label={title}
          onClick={() => {
            focusNext.current = "close";
            setManual(true);
          }}
          className={`${PLATE} flex h-8 w-full items-center gap-xs px-xs text-left text-xs transition-colors hover:bg-overlay`}
        >
          <StatusIcon tone={tone} />
          <span className={`min-w-0 flex-1 whitespace-nowrap ${HEAD} ${toneText(tone)}`}>
            <span aria-hidden="true">{uk.attention.title}</span>
            <CountDot n={formatCount(items.length)} />
          </span>
          <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-ink-3" />
        </button>
      )}
      <section
        id={bodyId}
        hidden={!expanded}
        aria-labelledby={headId}
        className={`${PLATE} px-xs py-2xs`}
      >
        {/* Одна строка: значок · «Потребує уваги · N справ» · крестик. Число с базой не рвётся (неразрывные пробелы в слове). */}
        <div className="flex h-8 items-center gap-xs">
          <StatusIcon tone={tone} />
          <h3 id={headId} aria-label={title} className={`min-w-0 flex-1 whitespace-nowrap ${HEAD} ${toneText(tone)}`}>
            <span aria-hidden="true">{uk.attention.title}</span>
            <CountDot n={formatCount(items.length)} />
          </h3>
          <button
            ref={closeRef}
            type="button"
            aria-expanded
            aria-controls={bodyId}
            aria-label={uk.attention.collapse}
            onClick={() => {
              markAll();
              focusNext.current = "line";
              setManual(false);
            }}
            className="relative inline-flex size-4 shrink-0 items-center justify-center rounded-control text-ink-3 transition-colors after:absolute after:-inset-2 after:content-[''] hover:bg-overlay hover:text-ink"
          >
            <X aria-hidden="true" className="size-3.5" />
          </button>
        </div>
        <ul className="-mx-xs mt-2xs flex max-h-36 flex-col gap-2xs overflow-y-auto [scrollbar-width:thin]">
          {items.map((one) => {
            const path = pathOf(one.screen);
            const row = rowOf(one);
            // Одна строка: точка тона · текст · (для «не знаю» — стрелка «Перевірити») · метка «нове».
            const body = (
              <>
                <span aria-hidden="true" className="inline-flex size-4 shrink-0 items-center justify-center">
                  <span className={`size-1.5 rounded-full ${one.tone === "alarm" ? "bg-alarm-ink" : "bg-warn-ink"}`} />
                </span>
                <span data-numbers className="text-ink">
                  {row.text}
                </span>
                {row.hint !== null && !row.unknown ? <span className="sr-only"> · {row.hint}</span> : null}
                {row.unknown ? (
                  <>
                    <ChevronRight aria-hidden="true" className="size-3 shrink-0 text-link" />
                    <span className="sr-only">
                      {row.hint} · {uk.attention.check}
                    </span>
                  </>
                ) : null}
                {isNew(one) ? (
                  <>
                    <span aria-hidden="true" className="ml-auto size-1.5 shrink-0 rounded-full bg-accent-line" />
                    <span className="sr-only">{uk.attention.newTag}</span>
                  </>
                ) : null}
              </>
            );
            const line = "flex items-center gap-xs whitespace-nowrap px-xs py-2xs text-xs";
            return (
              <li key={one.key} title={row.hint ?? undefined}>
                {path === undefined ? (
                  <span className={line}>{body}</span>
                ) : (
                  <Link
                    to={path}
                    {...(MERGED_INTO[one.screen] === undefined ? {} : { hash: MERGED_INTO[one.screen]!.hash })}
                    onClick={() => {
                      markOne(one.key);
                      onGo();
                    }}
                    className={`${line} rounded-control transition-colors hover:bg-overlay`}
                  >
                    {body}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
